import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, HStack, Text } from '@chakra-ui/react';

import { Guide } from '../../hooks/api/types';
import { useQueryParam } from '../../../../hooks/useQueryParams';
import useWindowSize from '../../hooks/useWindowSize';
import updateQueryParams from '../../../../helpers/updateQueryParams';
import { LongArrowLeftIcon } from '../../../../components/icons';

const timestamp = new Date().getTime();

const goToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

interface Params {
  guide: Guide;
}

const CmsGuide = forwardRef((params: Params, iframeRef) => {
  const { guide } = params;

  const qSectionNum = +useQueryParam('current-section')! || 0;
  const realIFrame = useRef<any>(null);
  const [totalSections, setTotalSections] = useState(0);
  const [currentSectionCounter, setCurrentSectionCounter] =
    useState(qSectionNum);
  const [iframeHeight, setIframeHeight] = useState(1000);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const currentSectionInterval = useRef<any>(null);

  const { width, height } = useWindowSize();

  useEffect(() => {
    setTimeout(() => {
      resizeIFrameToFitContent();
    }, 1000);
    setInterval(() => {
      resizeIFrameToFitContent();
    }, 5000);
  }, []);

  useEffect(() => {
    resizeIFrameToFitContent();
  }, [width, height]);

  const displayCurrentSection = () => {
    stopAllVideos();
    const sections = getSections();
    sections.forEach((section, index) => {
      section.classList.add('content_section_hidden');
    });
    sections.forEach((section, index) => {
      if (index === currentSectionCounter)
        section.classList.remove('content_section_hidden');
    });
  };

  const stopAllVideos = () => {
    realIFrame.current.contentWindow.postMessage('StopAllVideos');
  };

  const changeLinks = () => {
    const iframeDoc = realIFrame.current.contentWindow.document;
    iframeDoc.querySelectorAll('a').forEach((a: any) => {
      const href = a.getAttribute('href');
      if (href && href.startsWith('/this')) {
        const base = location.pathname.split('/').slice(0, 3);
        const newPath = href.split('/').slice(2);

        a.href = [...base, ...newPath].join('/');
      }
    });
  };

  useEffect(() => {
    displayCurrentSection();
    resizeIFrameToFitContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSectionCounter]);

  const resizeIFrameToFitContent = () => {
    if (!realIFrame.current?.contentWindow.document.body) {
      return;
    }
    const h = realIFrame.current.contentWindow.document.body.scrollHeight;
    setIframeHeight(h);

    realIFrame.current.height = h;
    realIFrame.current.style.minHeight = h + ' px !important';
  };

  const getSections = () => {
    const iframeDoc = realIFrame.current.contentWindow.document;
    return iframeDoc.querySelectorAll('.content_section') as any[];
  };

  useEffect(() => {
    if (currentSectionInterval.current) {
      clearInterval(currentSectionInterval.current);
    }
    if (isLoading) {
      currentSectionInterval.current = setInterval(() => {
        if (isLoading) {
          if (!realIFrame.current) {
            return;
          }
          const sections = getSections();
          setTotalSections(sections.length || 1);

          if (sections.length < currentSectionCounter)
            setCurrentSectionCounter(0);

          displayCurrentSection();
          resizeIFrameToFitContent();
        }
      }, 500);
    }
    return () => {
      if (currentSectionInterval.current) {
        clearInterval(currentSectionInterval.current);
      }
    };
  }, [isLoading]);

  const onCmsContentLoad = () => {
    setIsLoading(false);
    if (!realIFrame.current) {
      return;
    }
    const sections = getSections();
    setTotalSections(sections.length || 1);
    if (sections.length < currentSectionCounter) setCurrentSectionCounter(0);
    displayCurrentSection();
    resizeIFrameToFitContent();
    changeLinks();
  };

  const isBackDisabled = currentSectionCounter === 0;
  const isNextDisabled = currentSectionCounter === totalSections - 1;

  const next = () => {
    if (isNextDisabled) {
      return;
    }
    goToTop();
    if (currentSectionCounter === totalSections - 1) {
      // goToNextResource();
    } else {
      const queryParams = new URLSearchParams(location.search);
      queryParams.set(
        'current-section',
        (currentSectionCounter + 1).toString(),
      );
      updateQueryParams(queryParams);
      setCurrentSectionCounter((c) => c + 1);
    }
  };

  const back = () => {
    if (isBackDisabled) {
      return;
    }
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('current-section', (currentSectionCounter - 1).toString());
    updateQueryParams(queryParams);
    setCurrentSectionCounter((c) => c - 1);
    goToTop();
  };

  return (
    <div ref={iframeRef as any} className="cms_content_container">
      <iframe
        title="Other media via url"
        src={`/cms_content/${guide['pk']}/?toolbar_off&timestamp=${timestamp}`}
        ref={realIFrame}
        height={height}
        style={{
          marginBottom: 40,
          overflow: 'hidden',
          borderBottom: '1px solid rgba(213,214,222,0.5)',
          boxSizing: 'content-box',
        }}
        width="100%"
        frameBorder="0"
        allowFullScreen
        onLoad={() => onCmsContentLoad()}
      />
      {totalSections > 0 && (
        <HStack
          w="full"
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 40,
            marginTop: 20,
            paddingLeft: 30,
            paddingRight: 30,
          }}
        >
          <Button
            colorScheme="blue"
            disabled={isBackDisabled}
            onClick={() => back()}
            mr={4}
            leftIcon={<LongArrowLeftIcon boxSize="24px" />}
          >
            <span className={'back'}>Previous</span>
          </Button>
          <Text as="h4" textStyle="h4" style={{ marginRight: 16 }}>
            {currentSectionCounter + 1} / {totalSections}
          </Text>
          <Button
            colorScheme="blue"
            disabled={isNextDisabled}
            onClick={() => next()}
            rightIcon={
              <LongArrowLeftIcon
                boxSize="24px"
                ml={2}
                transform="rotate(180deg)"
              />
            }
          >
            <span className={'back'}>Next</span>
          </Button>
        </HStack>
      )}
    </div>
  );
});

export default CmsGuide;
